import yup from 'yup'
import compact from 'lodash/compact'
import uniq from 'lodash/uniq'

import { userApi } from '@atheneum/api'

function getExpertValidationSchema (expert, staticData) {
  return yup.object().shape({
    title: yup
      .string()
      .required('Title field is required')
      .typeError('Title field is required'),
    firstName: yup.string().required('First name field is required'),
    lastName: yup.string().required('Last name field is required'),
    email: yup
      .string()
      .nullable()
      .email('Email format is invalid')
      .test('unique', 'Email is already in use', value => {
        if (
          yup
            .string()
            .email()
            .isValidSync(value)
        ) {
          if (!value) return true // fallback to required error
          let payload = { email: value }
          if (expert && Number(expert.id)) {
            payload = { ...payload, id: expert.id }
          }
          return userApi.checkEmail(payload)
            .then(({ valid }) => valid)
            .catch(_ => false)
        }
        return true
      })
      .required('Email field is required'),
    address: yup
      .object()
      .when('expert.sendSmsReminders', {
        is: true,
        then: yup.object().shape({
          phones: yup.array().of(
            yup.object().shape({
              phoneTypeId: yup.number(),
              phoneNum: yup.string().when('phoneTypeId', {
                is: staticData.CONST.PHONE_TYPES.MOBILE,
                then: yup
                  .string()
                  .test(
                    'format for sending sms',
                    "Phone number must contain only digits, '+' sign and max length of 16 chars",
                    value => {
                      const regex = /^\+[1-9]{1}[0-9]{3,14}$/
                      return regex.test(value)
                    }
                  )
              })
            })
          )
        })
      })
      .shape({
        city: yup.string().required('City field is required'),
        phones: yup.array().of(
          yup.object().shape({
            phoneNum: yup
              .string()
              .matches(
                /^[0-9\-+/\s]*$/,
                "Phone number can contain only digits, '+', '-', ' ' and '/'"
              )
          })
        ),
        emails: yup.array().of(
          yup.object().shape({
            email: yup
              .string()
              .email('Email format is invalid')
              .test('unique', 'Email is already in use', value => {
                if (
                  yup
                    .string()
                    .email()
                    .isValidSync(value)
                ) {
                  if (!value) return true // fallback to required error
                  let payload = { email: value }
                  if (expert && Number(expert.id)) {
                    payload = { ...payload, id: expert.id }
                  }
                  return userApi.checkEmail(payload)
                    .then(({ valid }) => valid)
                    .catch(_ => false)
                }
                return true
              })
          })
        )
      }),
    links: yup.array().of(
      yup.object().shape({
        professionalLinkUrl: yup
          .string()
          .url('The Professional URL format is invalid')
      })
    ),
    phoneNotRepeating: yup
      .mixed()
      .test(
        'phoneNotRepeating',
        'You are using the same phone number in multiple fields',
        function () {
          const phones = compact(
            this.parent.address.phones.map(phone => phone.phoneNum)
          )
          const uniquePhones = compact(uniq(phones))

          return phones.length === uniquePhones.length
        }
      ),
    emailNotRepeating: yup
      .mixed()
      .test(
        'emailNotRepeating',
        'You are using the same email in multiple fields',
        function () {
          const primaryEmail = this.parent.email
          const restOfEmails = this.parent.address.emails.map(
            email => email.email
          )
          const emails = [primaryEmail, ...restOfEmails]

          const uniqueEmails = uniq(emails)

          return emails.length === uniqueEmails.length
        }
      ),
    expert: yup.object().shape({
      sendSmsReminders: yup.boolean().nullable(),
      subIndustries: yup.array().min(1, 'Subindustry field is required'),
      industryExpertise: yup
        .string()
        .required('Industry experience field is required.'),
      hourlyRate: yup
        .number()
        .nullable()
        .positive('Hourly rate amount must be a positive number')
        .typeError('Hourly rate amount must be a number')
    }),
    languages: yup.array().of(
      yup.object().shape({
        id: yup
          .number()
          .required('Language field is required')
          .typeError('Language field is required'),
        languageToUser: yup.object().shape({
          languageProficiencyId: yup
            .number()
            .required('Language proficiency field is required')
            .typeError('Language proficiency field is required')
        })
      })
    ),
    experiences: yup
      .array()
      .of(
        yup.object().shape({
          position: yup.object().typeError('Position field is required'),
          company: yup.object().typeError('Company field is required'),
          startDate: yup
            .string()
            .required('Start date field is required')
            .typeError('Start date field is required'),
          endDate: yup
            .mixed()
            .when('current', {
              is: false,
              then: yup
                .string()
                .required('End date field or Current is required')
                .typeError('End date field or Current is required')
            })
            .when('current', {
              is: true,
              then: yup.number().nullable()
            }),
          current: yup.boolean()
        })
      )
      .min(1)
  })
}

export default getExpertValidationSchema
